<template>
  <v-container fluid grid-list-md text-left>
    <v-tooltip right color="red darken-2">
      <template v-slot:activator="{ on }">
        <v-btn
          v-on="on"
          @click="onDisable"
          small
          fab
          color="red darken-2"
          dark
          style="margin-top: -30px;"
        >
          <v-icon medium>keyboard_backspace</v-icon>
        </v-btn>
      </template>
      <span class="font-weight-bold body-2 white--text">
        Back to configuration</span
      >
    </v-tooltip>

    <v-layout row wrap>
      <!-- timeline ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
      <v-flex xs12 sm12 md3 fill-height>
        <GuidanceSchedule
          v-if="loaded"
          title="Schedule"
          :alerts="false"
          :intervals="rawData.intervals"
          :rampUpMinutes="rampUpMinutes"
          :rampDownMinutes="rampDownMinutes"
          :intervalLength="rawData.facility.intervalLengthSeconds"
          :expanded="true"
          :microgrid="microgrid"
          :key="`schedule-${componentKey}`"
        ></GuidanceSchedule>
      </v-flex>

      <!-- chart & table ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
      <v-flex d-flex xs12 sm12 md9 v-if="loaded">
        <v-layout row wrap>
          <v-flex>
            <!-- chart ~*~*~*~*~*~*~*~*~*~*~* -->
            <GuidanceChart
              v-if="loaded"
              :alerts="false"
              :intervals="rawData.intervals"
              :calculated="meta.calculatedAt"
              :intervalLength="rawData.facility.intervalLengthSeconds"
              :rampUpMinutes="rampUpMinutes"
              :rampDownMinutes="rampDownMinutes"
              :key="`chart-${componentKey}`"
            ></GuidanceChart>
            <!-- table ~*~*~*~*~*~*~*~*~*~*~* -->
            <GuidanceTable
              :intervals="rawData.intervals"
              :key="`table-${componentKey}`"
            ></GuidanceTable>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <!-- constraints slide panel ~*~*~*~*~*~*~*~*~*~*~*~* -->
    <GuidanceConfigPopout
      :json="rawData.facility.configuration"
      :key="`config-${componentKey}`"
      v-model="rawData"
      v-if="loaded"
    ></GuidanceConfigPopout>
  </v-container>
</template>
<script>
import MockDataService from '@/services/MockDataService'
import { mapGetters } from 'vuex'
import moment from 'moment'
export default {
  name: 'GuidanceDashboard',
  props: {
    exId: {
      type: Number,
      required: true
    },
    microgrid: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      calculatedTotalValue: 0,
      sheet: false,
      currentTime: null,
      rawData: '',
      matrix: [],
      meta: {
        guidanceReportId: '',
        reportDate: '',
        calculatedAt: '',
        runsChangedSinceLastReport: false
      },
      loaded: false,
      error: false,
      componentKey: 0,
      rampUpMinutes: null,
      rampDownMinutes: null
    }
  },
  computed: {
    ...mapGetters(['getFacilityTimezone', 'getNcpCeiling'])
  },
  created() {
    this.init()
    this.fetch()
  },
  watch: {
    rawData() {
      this.parse()
      this.parseConfigForRamping()
    },
    exId() {
      this.init()
      this.fetch()
    }
  },
  methods: {
    init() {
      this.currentTime = this.$_datetimeMethod_currentTimeInZone(
        this.getFacilityTimezone,
        'YYYY-MM-DD HH:mm'
      )
    },
    fetch() {
      this.rawData = []
      this.rawData = MockDataService.getGuidanceData(this.exId, true)
    },
    reset() {
      this.loaded = false
      this.error = false
      this.calculatedTotalValue = 0
    },
    parse() {
      // console.log('GuidanceDashboard parse()')
      // clear data
      this.loaded = false
      this.error = false
      this.calculatedTotalValue = 0
      // grab & store meta data
      this.meta.guidanceReportId = this.rawData.guidanceReportId
      this.meta.reportDate = this.rawData.reportDate
      this.meta.calculatedAt = moment(this.currentTime)
        .subtract(27, 'minutes')
        .format('YYYY-MM-DD HH:mm:ss')
      this.meta.runsChangedSinceLastReport = this.rawData.runsChangedSinceLastReport
      // grab, calculate and store interval (related) data
      this.preProcessIntervals()
      // forces component keys to update and component refreshes
      this.componentKey += 1
      // elvis leaves the bulding...
      this.loaded = true
    },
    // make data arrays for timeline and chart use
    preProcessIntervals() {
      for (var x = 0; x < this.rawData.intervals.length; x++) {
        let interval = this.rawData.intervals[x]

        // loadForecastKw same for all programs / intervals
        // loadForecastKw = peakDemand * loadForecastMultiplier
        interval.loadForecastKw =
          interval.loadForecastMultiplier * this.microgrid.peakDemand

        // targetOutputKw and value differ per scenario / per program, default
        interval.targetOutputKw = 0.0
        interval.totalValueAtStake = 0.0

        // only execute interval & program calculations when RUN event
        if (interval.guidance === 'MUST_RUN') {
          // targetOutputKw is same across board
          interval.targetOutputKw =
            this.microgrid.solar * interval.solarMultiplier +
            this.microgrid.generation * interval.generatedMultiplier +
            this.microgrid.demandMgmt * interval.demandMultiplier +
            this.microgrid.storage * interval.storageMultiplier

          // generated <= peak demand
          if (this.exId === 8) {
            this.runGeneratedLowerCalcs(interval)
          }
          // generated > peak demand
          else {
            this.runGeneratedHigherCalcs(interval)
          }

          // keep a running total of value to post to parent when parsing done
          this.calculatedTotalValue += parseInt(interval.totalValueAtStake)
          this.calculatedTotalValue += parseInt(interval.totalValueAtStake)
        }
      }
      this.$parent.guidanceTotalValue = this.calculatedTotalValue
    },
    runGeneratedLowerCalcs(interval) {
      // targetOutputKw = generated * 0.97  + solar * 0.625 + demandMgmt
      // interval.targetOutputKw =
      //   this.microgrid.generation * 0.97 +
      //   this.microgrid.solar * 0.625 +
      //   this.microgrid.demandMgmt

      // parse programs
      for (var g = 0; g < interval.programs.length; g++) {
        let program = interval.programs[g]
        // programKw
        program.programKw = interval.targetOutputKw
        // value
        program.valueAtStake =
          (program.programKw * program.programPriceMw) / 1000
        // increment total value
        interval.totalValueAtStake += program.valueAtStake
      }
    },
    runGeneratedHigherCalcs(interval) {
      // parse programs

      let dayAheadKw = 0
      for (var g = 0; g < interval.programs.length; g++) {
        let program = interval.programs[g]
        // programKw
        if (this.isPeakShaving(program)) {
          program.programKw = interval.loadForecastKw
        }
        if (program.description === 'Day-Ahead') {
          let kw = interval.targetOutputKw - interval.loadForecastKw - 500
          program.programKw = Math.round(kw / 1000) * 1000
          dayAheadKw = program.programKw
        }
        // assumes RT is after DA in program list, ok for demo, not real world
        if (program.description === 'Real-Time') {
          program.programKw =
            interval.targetOutputKw - dayAheadKw - interval.loadForecastKw
        }
        // value
        program.valueAtStake =
          (program.programKw * program.programPriceMw) / 1000
        // increment total value
        interval.totalValueAtStake += program.valueAtStake
      }
    },
    includesPeakShavingProgram(programs) {
      let ret = false
      for (var pr = 0; pr < programs.length; pr++) {
        if (this.isPeakShaving(programs[pr])) {
          ret = true
          break
        }
      }
      return ret
    },
    isPeakShaving(program) {
      let ret = false
      if (program.description.indexOf('Peak-Shaving') > -1) {
        ret = true
      }
      return ret
    },

    onDisable() {
      this.$parent.dashboardOpen = false // methods.toggleDashboard()
      //   this.$parent.$options.methods.toggleDashboard()
      //   this.$parent.$emit('toggleDashboard')
    },
    parseConfigForRamping() {
      this.rampUpMinutes = 30
      this.rampDownMinutes = 15
      let s1 = JSON.stringify(this.rawData.facility.configuration)
      let s2 = s1.replace(new RegExp('{', 'g'), '')
      let s3 = s2.replace(new RegExp('}', 'g'), '')
      let s4 = s3.replace(new RegExp('"', 'g'), '')
      let s5 = s4.split(',')
      for (var c = 0; c < s5.length; c++) {
        let key = s5[c].split(':')[0]
        let val = s5[c].split(':')[1]
        if (key == 'rampUpMinutes') {
          this.rampUpMinutes = parseInt(val)
        }
        if (key == 'rampDownMinutes') {
          this.rampDownMinutes = parseInt(val)
        }
      }
    }
  }
}
</script>
